import { action, computed, observable } from 'mobx';
import { first, last } from 'lodash';
import type { CXContractBlock } from '~/components/ClientExperience/OneFlow/models/cx-contract-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import {
    IPageInfoView,
    IPageSubmission,
    ITransformedPageResponse
} from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { CXContainerBlock } from '~/components/ClientExperience/OneFlow/models/cx-container-block.model';
import type { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import type { CXScheduleContainerBlock } from '~/components/ClientExperience/OneFlow/models/cx-schedule-container-block.model';
import type { CXServiceSelectionBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-block.model';

export class CXPage {
    rootStore: CXRootStore;
    @observable page_id: string;
    @observable title: string;
    @observable order: number;
    @observable root_block_id: string;
    @observable cta_text: string;
    @observable completed: boolean;
    @observable flowRevision: FlowRevision<CXRootStore>;
    @observable submissions: IPageSubmission[];
    @observable views: IPageInfoView[];

    constructor(
        page: ITransformedPageResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        this.rootStore = rootStore;

        if (!page) {
            return;
        }

        this.page_id = page.page_id;
        this.title = page.title;
        this.order = page.order;
        this.root_block_id = page.root_block_id;
        this.cta_text = page.cta_text;
        this.completed = page.completed;
        this.flowRevision = flowRevision;
        this.submissions = page.submissions;
        this.views = page.views;
    }

    @computed
    get rootBlock() {
        return this.flowRevision.blocksMap[
            this.root_block_id
        ] as CXContainerBlock;
    }

    @computed
    get blocks() {
        return this.rootBlock.allDescendants().concat([this.rootBlock]);
    }

    @action
    setCompleted(isCompleted: boolean) {
        this.completed = isCompleted;
    }

    @computed
    get isAllPendingClientBlocksComplete() {
        return this.blocks.every(block => !block.isPendingClientCompletion);
    }

    @action
    setFlowRevision(flowRevision: FlowRevision<CXRootStore>) {
        this.flowRevision = flowRevision;
    }

    @computed
    get background_image() {
        return this.rootBlock?.background_image;
    }

    @computed
    get styles() {
        return this.rootBlock.styles;
    }

    @computed
    get isFirstPage() {
        return first(this.flowRevision.pages).page_id === this.page_id;
    }

    @computed
    get isLastPage() {
        return last(this.flowRevision.pages).page_id === this.page_id;
    }

    @computed
    get lastRootBlock() {
        return last(this.rootBlock.childBlocks);
    }

    @computed
    get invoiceContainerBlock() {
        return this.blocks.find(
            block => block.isInvoiceContainerBlock
        ) as CXInvoiceBlock;
    }

    @computed
    get contractBlock() {
        return this.blocks.find(
            block => block.isContractBlock
        ) as CXContractBlock;
    }

    @computed
    get indexInSortedPages() {
        return this.flowRevision.pages.findIndex(
            page => page.page_id === this.page_id
        );
    }

    @computed
    get scheduleContainerBlock(): CXScheduleContainerBlock {
        return this.blocks.find(
            block => block.isScheduleContainerBlock
        ) as CXScheduleContainerBlock;
    }

    @computed
    get isContractPage() {
        return !!this.contractBlock;
    }

    @computed
    get payContainerBlock() {
        return this.blocks.find(block => block.isPayContainerBlock);
    }

    @computed
    get isServiceSelectionPage(): boolean {
        return !!this.blocks.find(block => block.isServiceSelectionBlock);
    }
    @computed
    get serviceSelectionBlocks() {
        return this.blocks.filter(
            block => block.isServiceSelectionBlock
        ) as CXServiceSelectionBlock[];
    }

    @computed
    get sessionSelectionBlock() {
        return this.serviceSelectionBlocks.find(
            block => block.isSessionSelection
        );
    }

    @computed
    get isAnyServiceItemSelectedOnPage() {
        return (
            this.isServiceSelectionPage &&
            this.blocks.some(
                block =>
                    block.type === 'service_selection_block' &&
                    !!block.connectedVariable?.value?.selected_services?.length
            )
        );
    }

    @computed
    get isInvoicePage(): boolean {
        return !!this.invoiceContainerBlock;
    }

    @computed
    get isSchedulePage(): boolean {
        return !!this.scheduleContainerBlock;
    }

    @computed
    get isPaymentPage(): boolean {
        return !!this.payContainerBlock?.block_id;
    }

    @computed
    get hasQuestionBlocks() {
        return this.blocks.some(block => block.isQuestionBlock);
    }

    @computed
    get displayOrder() {
        return this.rootStore.flowViewUiStore.selectedFlow.pages.findIndex(
            page => page.page_id === this.page_id
        );
    }

    getActionBlockOnPageForPrint() {
        return this.blocks.find(block => block.isActionBlockForPrint);
    }

    getActionBlockTypeOnPageForPrint() {
        const actionBlockTypesToString = {
            contract_block: 'contract',
            service_selection_block: 'services',
            invoice_container_block: 'invoice'
        };

        const actionBlock = this.getActionBlockOnPageForPrint();

        if (actionBlock) {
            return actionBlockTypesToString[actionBlock.type];
        }

        return this.hasQuestionBlocks ? 'questions' : 'only_content';
    }

    @computed
    get isLeadDetailsPage() {
        return this.blocks?.some(block => block.isLeadDetailsBlock());
    }

    // methid that shouldn't be in use. Added for pairity with Page model
    // I wanted to throw an error here but that messed with TS so we can
    // add a manual report to DD or something
    get linkedFields() {
        return [];
    }
}
